import { ExportFormat } from '@dtx-company/flow-codegen/src/code/generated.types'
import { useCodeSelector } from '@dtx-company/inter-app/src/hooks/useCodeSelector'
import {
  usePrintStorePreviewImageUrlQuery,
  usePrintStoreTemplateDetailsQuery
} from '@dtx-company/inter-app/src/redux/slices/printStoreApiSlice/printStoreQueries'

export interface UseTemplatePreviewImageReturn {
  aspectRatio: number
  thumbnailUrl: string | undefined
}

export const useTemplatePreviewImage = ({
  dhProjectId,
  templateId
}: {
  dhProjectId?: string
  templateId?: string
}): UseTemplatePreviewImageReturn => {
  const { dhProjectImage } = useCodeSelector(state => state.printStore)

  /**
   * If the dhProjectImage is not available, we need to fetch the template details
   * in order to calculate the aspect ratio of the preview image.
   */
  const { previewHeight, previewWidth } = usePrintStoreTemplateDetailsQuery(
    { templateId: String(templateId) },
    {
      skip: !!dhProjectImage || !templateId,
      selectFromResult: ({ data }) => {
        return {
          previewHeight: data?.getTemplate?.height ?? 7,
          previewWidth: data?.getTemplate?.width ?? 5
        }
      }
    }
  )

  const aspectRatio = dhProjectImage
    ? dhProjectImage.width / dhProjectImage.height
    : previewWidth / previewHeight

  /**
   * If the dhProjectImage is not available, we need to fetch the temporary image url
   * in order to display the preview image.
   */
  const { previewImageUrl } = usePrintStorePreviewImageUrlQuery(
    { dhProjectId: String(dhProjectId), format: ExportFormat.Jpg },
    {
      skip: !!dhProjectImage || !dhProjectId,
      selectFromResult: ({ data }) => {
        return {
          previewImageUrl: data?.getImageUrl?.imageUrl
        }
      }
    }
  )

  const thumbnailUrl = dhProjectImage?.url ?? previewImageUrl

  return {
    aspectRatio,
    thumbnailUrl
  }
}
